/**
 * @fileoverview PlayerButton component for the Storyteller web application.
 * Displays a player button with a play icon and a label.
 *
 * @example
 * // Basic usage
 * <PlayerButton label="Play" onClick={() => {}} />
 */

import css from './PlayerButton.module.css'

/**
 * @interface PlayerButtonProps
 * @description Props for the PlayerButton component.
 * @property {string} label - The label of the player button.
 * @property {boolean} [isDisabled] - Whether the player button is disabled.
 * @property {Function} onClick - The function to call when the player button is clicked.
 * @extends {React.HTMLAttributes<HTMLDivElement>}
 */
export interface PlayerButtonProps extends React.HTMLAttributes<HTMLDivElement> {
    label: string
    isDisabled?: boolean
    onClick: () => void
}

/**
 * @function PlayerButton
 * @description Component for displaying a player button with a play icon and a label.
 * @param {PlayerButtonProps} props - The props for the PlayerButton component.
 * @returns {JSX.Element} The PlayerButton component.
 */
export function PlayerButton(props: PlayerButtonProps) {
    return (
        <div className={css.container}>
            <div className={css.player_outside}>
                <button
                    data-testid="play-button"
                    disabled={props.isDisabled}
                    onClick={props.onClick}
                    className={`${css.button} ${css.player}`}
                >
                    <p className={css.button_text}>{props.label}</p>
                    <div className={`${css.overlay} ${css.player_overlay}`}>
                        <div className={css.player_overlay_icon} />
                    </div>
                </button>
            </div>
        </div>
    )
}
